import { useEffect, useMemo, useState } from 'react'
import { ShieldQuestion } from 'lucide-react'
import { ResultBadge } from '../styles'
import { extractFraudChecks } from '../utils'
import { ConfiguringChecklist } from './ConfiguringChecklist'
import { InteractionDiagram } from './InteractionDiagram'
import { FraudConfigPanel } from './FraudConfigPanel'
import { FraudGuidedView } from './FraudGuidedView'
import { ExperienceModeSwitcher } from './ExperienceModeSwitcher'
import type { ExperienceMode } from './ExperienceModeSwitcher'
import { ExistingCustomerPanel } from './ExistingCustomerPanel'
import type { ExistingCustomerSelection } from './ExistingCustomerPanel'
import type { CustomerMode } from './NewExistingToggle'
import { StageSaveBar } from './StageSaveBar'
import { CONFIG_CHECKLISTS, DEFAULT_FRAUD_SIGNALS } from '../data'
import { buildFraudDiagramDsl } from '../dynamicInteractionDiagrams'
import type { PlatformJourneyRecord } from '@api/client'
import type { AnyObj, FraudSignalConfig } from '../types'
import type { AiConfigureSuggestions, AiIdSuggestion, AiStageSuggestionState } from '../aiConfigure'
import { AiSuggestionBanner } from './AiSuggestionBanner'

const FRAUD_CHECK_LABELS: Record<string, string> = {
  FRAUD_DEVICE: 'Device',
  FRAUD_IP_ADDRESS: 'IP Address',
  FRAUD_EMAIL_ADDRESS: 'Email',
  FRAUD_PHONE_NUMBER: 'Phone',
}

const RISK_LEVEL_STYLE: Record<string, { bg: string; color: string }> = {
  LOW: { bg: 'rgba(52,211,153,.15)', color: '#15803d' },
  MEDIUM: { bg: 'rgba(251,146,60,.15)', color: '#c2410c' },
  HIGH: { bg: 'rgba(248,113,113,.15)', color: '#b91c1c' },
  UNACCEPTABLE: { bg: 'rgba(248,113,113,.25)', color: '#7f1d1d' },
}

function RiskLevelBadge({ level }: { level: string }) {
  const style = RISK_LEVEL_STYLE[level] ?? { bg: 'rgba(148,163,184,.15)', color: '#475569' }
  return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: style.bg, color: style.color }}>{level}</span>
}

interface FraudStageProps {
  workflowResult: AnyObj | null
  customerMode: CustomerMode
  existingCustomer: ExistingCustomerSelection | null
  customerName: string | null
  platformVersion: 'v1' | 'v2'
  experienceMode: ExperienceMode
  onExperienceModeChange: (mode: ExperienceMode) => void
  journeyId: number | null
  journeyRecord: PlatformJourneyRecord | null
  onJourneySaved: () => void
  aiSuggestions?: AiConfigureSuggestions | null
  /** Lifted to the tool shell so Account Setup's checklist and this stage's
   * own banner share one apply/dismiss state instead of drifting apart. */
  fraudAi: AiStageSuggestionState<AiIdSuggestion[]>
}

export function FraudStage({
  workflowResult, customerMode, existingCustomer, customerName, platformVersion,
  experienceMode, onExperienceModeChange, journeyId, journeyRecord, onJourneySaved,
  aiSuggestions, fraudAi,
}: FraudStageProps) {
  const [fraudSignals, setFraudSignals] = useState<FraudSignalConfig[]>(DEFAULT_FRAUD_SIGNALS)
  const [checklistEnabled, setChecklistEnabled] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(CONFIG_CHECKLISTS.fraud.map((i) => [i.id, true]))
  )
  function toggleChecklistItem(id: string) {
    setChecklistEnabled((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  useEffect(() => {
    if (!journeyRecord) return
    if (journeyRecord.fraudConfig) setFraudSignals(journeyRecord.fraudConfig as FraudSignalConfig[])
    if (journeyRecord.fraudChecklist) setChecklistEnabled(journeyRecord.fraudChecklist as Record<string, boolean>)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyRecord?.id])
  const diagramDsl = useMemo(
    () => buildFraudDiagramDsl({ platformVersion, signals: fraudSignals }),
    [platformVersion, fraudSignals],
  )
  const checks = extractFraudChecks(workflowResult)

  const fraudAiBanner = fraudAi.visible && aiSuggestions?.fraud && (
    <AiSuggestionBanner
      rationale={aiSuggestions.fraud.rationale}
      confidence={aiSuggestions.fraud.confidence}
      applied={fraudAi.applied}
      onApply={fraudAi.apply}
      onDismiss={fraudAi.dismiss}
    />
  )

  const technicalDetailsNode = (
    <>
      {fraudAiBanner}
      <FraudConfigPanel onSignalsChange={setFraudSignals} aiSeed={fraudAi.seed} />
      <InteractionDiagram stageId="fraud" dsl={diagramDsl} />
      {checks.length > 0 && (
        <div className="rounded-[14px] overflow-hidden p-4 space-y-3" style={{ border: '1px solid rgba(167,139,250,.3)', background: 'rgba(167,139,250,.04)' }}>
          <div className="flex items-center gap-2">
            <ShieldQuestion size={13} className="text-violet-700" />
            <p className="text-[10px] font-semibold uppercase tracking-wider text-violet-700">Fraud / Device Risk</p>
          </div>
          <div className="space-y-2">
            {checks.map((check: AnyObj, i: number) => {
              const label = FRAUD_CHECK_LABELS[check.class] ?? check.class ?? '—'
              const suppData: Record<string, string> = check.supplementaryData ?? check.providerResult?.supplementaryData ?? {}
              const riskLevel: string | undefined = check.riskLevel ?? suppData['Risk.Level']
              return (
                <div key={i} className="rounded-[10px] p-3 space-y-1.5" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-mono font-semibold text-[#1e293b]">{label}</span>
                    <span className="text-[10px] font-mono text-[#64748b]">({check.class})</span>
                    {check.result && <ResultBadge result={check.result} />}
                    {riskLevel && <RiskLevelBadge level={riskLevel} />}
                  </div>
                  {check.class === 'FRAUD_DEVICE' && Object.keys(suppData).length > 0 && (
                    <table className="w-full text-[10px]">
                      <tbody>
                        {Object.entries(suppData).map(([k, v]) => (
                          <tr key={k} className="border-t" style={{ borderColor: '#e5e7eb' }}>
                            <td className="py-1 pr-3 font-mono text-[#64748b] w-44">{k}</td>
                            <td className="py-1 font-mono text-[#475569]">{v}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )

  return (
    <div className="space-y-3">
      {customerMode === 'existing' ? (
        <div className="space-y-3">
          <ExistingCustomerPanel
            productKey="fraud"
            sections={['Rules']}
            title="Fraud — Existing Customer"
            description="Shows this customer's Rules & Fraud settings as scoped in DealHub (fraud enabled, fraud signal types, requirements). Reads DealHub's own presales record, not FrankieOne's live platform."
            showCascadeSummary={false}
            sharedSelection={existingCustomer}
            searchable={false}
          />
          {fraudAiBanner}
          <FraudConfigPanel onSignalsChange={setFraudSignals} aiSeed={fraudAi.seed} />
        </div>
      ) : (
      <>
      <ExperienceModeSwitcher mode={experienceMode} onChange={onExperienceModeChange} />
      <StageSaveBar
        journeyId={journeyId}
        buildFields={() => ({ fraudConfig: fraudSignals, fraudChecklist: checklistEnabled })}
        onSaved={onJourneySaved}
      />

      {experienceMode === 'guided' && (
        <FraudGuidedView
          workflowResult={workflowResult}
          checks={checks}
          customerName={customerName}
          checklistItems={CONFIG_CHECKLISTS.fraud}
          checklistEnabled={checklistEnabled}
          onToggleChecklistItem={toggleChecklistItem}
          technicalDetails={technicalDetailsNode}
        />
      )}

      {experienceMode === 'classic' && (
      <>
      <ConfiguringChecklist productLabel="Fraud" items={CONFIG_CHECKLISTS.fraud} enabled={checklistEnabled} onToggle={toggleChecklistItem} customerName={customerName} />
      {fraudAiBanner}
      <FraudConfigPanel onSignalsChange={setFraudSignals} aiSeed={fraudAi.seed} />
      <InteractionDiagram stageId="fraud" dsl={diagramDsl} />

      {!workflowResult ? (
        <div className="flex flex-col items-center justify-center py-10 rounded-[14px]" style={{ border: '2px dashed #e5e7eb', background: '#f8fafc' }}>
          <ShieldQuestion size={22} className="text-[#94a3b8] mb-3" />
          <p className="text-[13px] font-semibold text-[#64748b]">Run the KYC workflow first</p>
          <p className="text-[11px] text-[#94a3b8] mt-1.5 text-center max-w-sm">
            Device, IP, email and phone risk checks run as part of the same workflow execution.
          </p>
        </div>
      ) : checks.length === 0 ? (
        <div className="p-4 rounded-[14px]" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
          <p className="text-[12px] text-[#475569]">This workflow didn&apos;t include any fraud/device checks.</p>
        </div>
      ) : (
        <div className="rounded-[14px] overflow-hidden p-4 space-y-3" style={{ border: '1px solid rgba(167,139,250,.3)', background: 'rgba(167,139,250,.04)' }}>
          <div className="flex items-center gap-2">
            <ShieldQuestion size={13} className="text-violet-700" />
            <p className="text-[10px] font-semibold uppercase tracking-wider text-violet-700">Fraud / Device Risk</p>
          </div>
          <div className="space-y-2">
            {checks.map((check: AnyObj, i: number) => {
              const label = FRAUD_CHECK_LABELS[check.class] ?? check.class ?? '—'
              const suppData: Record<string, string> = check.supplementaryData ?? check.providerResult?.supplementaryData ?? {}
              const riskLevel: string | undefined = check.riskLevel ?? suppData['Risk.Level']
              return (
                <div key={i} className="rounded-[10px] p-3 space-y-1.5" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-mono font-semibold text-[#1e293b]">{label}</span>
                    <span className="text-[10px] font-mono text-[#64748b]">({check.class})</span>
                    {check.result && <ResultBadge result={check.result} />}
                    {riskLevel && <RiskLevelBadge level={riskLevel} />}
                  </div>
                  {check.class === 'FRAUD_DEVICE' && Object.keys(suppData).length > 0 && (
                    <table className="w-full text-[10px]">
                      <tbody>
                        {Object.entries(suppData).map(([k, v]) => (
                          <tr key={k} className="border-t" style={{ borderColor: '#e5e7eb' }}>
                            <td className="py-1 pr-3 font-mono text-[#64748b] w-44">{k}</td>
                            <td className="py-1 font-mono text-[#475569]">{v}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
      </>
      )}
      </>
      )}
    </div>
  )
}
